import { useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProductColorEditor from './ProductColorEditor';
import { mapProductColors } from '../services/productVariantMediaService';

const { FiFolderPlus, FiImage, FiVideo, FiX } = FiIcons;

const defaults = {
  name: '',
  slug: '',
  description: '',
  price: '',
  category_id: '',
  status: 'active',
  featured: false,
  quantity_on_hand: 10,
  low_stock_threshold: 2,
  media: [],
  colors: []
};

function ProductModal({
  categories,
  initial,
  onClose,
  onSave,
  onCreateCategory
}) {
  const initialColors = useMemo(() => mapProductColors(
    initial?.variants || [],
    initial?.media || [],
    initial?.inventory || []
  ), [initial]);

  const baseInventory = initial?.inventory?.find(
    (item) => !item.variant_id
  );

  const [values, setValues] = useState({
    ...defaults,
    ...initial,
    quantity_on_hand:
      baseInventory?.quantity_on_hand
      ?? initial?.quantity_on_hand
      ?? 10,
    low_stock_threshold:
      baseInventory?.low_stock_threshold
      ?? initial?.low_stock_threshold
      ?? 2,
    media: (initial?.media || [])
      .filter((item) => !item.variant_id)
      .map((item) => ({ ...item })),
    colors: initialColors
  });

  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState('image');
  const [newCategory, setNewCategory] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const highlightVideo = values.media.find(
    (item) => item.media_type === 'video'
  );

  const updateHighlightVideo = (url) => {
    setValues((current) => {
      const existing = current.media.find(
        (item) => item.media_type === 'video'
      );
      const photos = current.media.filter(
        (item) => item.media_type !== 'video'
      );
      const media = url.trim()
        ? [
          ...photos,
          {
            ...existing,
            id: existing?.id || `temp-${crypto.randomUUID()}`,
            media_url: url,
            media_type: 'video',
            alt_text: `${current.name || 'ZEVI'} highlight video`,
            is_primary: false,
            sort_order: photos.length
          }
        ]
        : photos;
      return { ...current, media };
    });
  };

  const update = (event) => {
    const { name, type, checked, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const updateName = (event) => {
    const name = event.target.value;
    setValues((current) => ({
      ...current,
      name,
      slug: current.id
        ? current.slug
        : name
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '')
    }));
  };

  const addMedia = () => {
    const url = mediaUrl.trim();
    if (!url) return;
    if (mediaType === 'video') {
      updateHighlightVideo(url);
      setMediaUrl('');
      return;
    }
    setValues((current) => ({
      ...current,
      media: [
        ...current.media,
        {
          id: `temp-${crypto.randomUUID()}`,
          media_url: url,
          media_type: mediaType,
          alt_text: `${current.name || 'ZEVI'} saree`,
          is_primary: current.media.length === 0,
          sort_order: current.media.length
        }
      ]
    }));
    setMediaUrl('');
  };

  const removeMedia = (index) => {
    setValues((current) => {
      const media = current.media
        .filter((_, itemIndex) => itemIndex !== index)
        .map((item, itemIndex) => ({
          ...item,
          is_primary: itemIndex === 0,
          sort_order: itemIndex
        }));
      return { ...current, media };
    });
  };

  const createCategory = async () => {
    if (!categoryName.trim()) return;
    try {
      const category = await onCreateCategory(categoryName.trim());
      setValues((current) => ({
        ...current,
        category_id: category?.id || current.category_id
      }));
      setCategoryName('');
      setNewCategory(false);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setMessage('');

    const colorsWithoutMedia = values.colors.filter(
      (color) => !(color.media || []).some(
        (item) => String(item.media_url || '').trim()
      )
    );

    if (!values.name.trim()) {
      setMessage('Product name is required.');
      return;
    }
    if (!values.price || Number(values.price) <= 0) {
      setMessage('Please enter a valid price.');
      return;
    }
    if (values.colors.length > 0 && colorsWithoutMedia.length > 0) {
      setMessage(
        `Add at least one photo for: ${
          colorsWithoutMedia.map((c) => c.name).join(', ')
        }.`
      );
      return;
    }
    if (!values.media.length && !values.colors.length) {
      setMessage('Please add at least one product or color photo.');
      return;
    }

    setSaving(true);
    try {
      await onSave(values);
      onClose();
    } catch (error) {
      setMessage(error.message);
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal-card product-editor-modal">
        <header className="modal-header">
          <div>
            <span className="modal-eyebrow">
              {values.id ? 'EDIT PRODUCT' : 'NEW PRODUCT'}
            </span>
            <h2>{values.id ? `Edit: ${values.name}` : 'Add New Saree'}</h2>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close product editor"
          >
            <SafeIcon icon={FiX} />
          </button>
        </header>

        <form className="modal-form" onSubmit={submit}>
          {message && <div className="admin-alert error">{message}</div>}

          <section className="form-section-box">
            <h4 className="section-title">1. Product Details</h4>
            <div className="form-grid-2">
              <label className="admin-field-label">
                Product Name *
                <input
                  name="name"
                  required
                  value={values.name}
                  onChange={updateName}
                />
              </label>
              <label className="admin-field-label">
                Category
                <select
                  name="category_id"
                  value={values.category_id || ''}
                  onChange={update}
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option value={category.id} key={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!newCategory ? (
              <button
                type="button"
                className="admin-btn-secondary small"
                onClick={() => setNewCategory(true)}
              >
                <SafeIcon icon={FiFolderPlus} />
                Create New Category
              </button>
            ) : (
              <div className="inline-category-row">
                <input
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  placeholder="New category name"
                />
                <button type="button" onClick={createCategory}>Save</button>
                <button type="button" onClick={() => setNewCategory(false)}>
                  Cancel
                </button>
              </div>
            )}

            <div className="form-grid-2 product-editor-spacing">
              <label className="admin-field-label">
                Base Price (LKR) *
                <input
                  name="price"
                  type="number"
                  min="0"
                  required
                  value={values.price}
                  onChange={update}
                />
              </label>
              <label className="admin-field-label">
                Status
                <select
                  name="status"
                  value={values.status}
                  onChange={update}
                >
                  <option value="active">Active (Visible)</option>
                  <option value="draft">Draft (Hidden)</option>
                  <option value="archived">Archived</option>
                </select>
              </label>
            </div>

            <label className="admin-field-label full-width">
              Description
              <textarea
                name="description"
                rows="4"
                value={values.description}
                onChange={update}
              />
            </label>

            <label className="admin-checkbox-label">
              <input
                name="featured"
                type="checkbox"
                checked={values.featured}
                onChange={update}
              />
              Feature this product on homepage
            </label>
          </section>

          <section className="form-section-box">
            <h4 className="section-title">2. Colors &amp; Color Media</h4>
            <p className="product-editor-help">
              Add each color, then add multiple photos or video URLs beneath
              it. Selecting a color on the product page shows only its
              assigned media. Videos are optional.
            </p>
            <ProductColorEditor
              colors={values.colors}
              onChange={(colors) => setValues((current) => ({
                ...current,
                colors
              }))}
            />
          </section>

          {values.colors.length === 0 && (
            <section className="form-section-box">
              <h4 className="section-title">
                3. Stock for Product Without Colors
              </h4>
              <div className="form-grid-2">
                <label className="admin-field-label">
                  Available Units *
                  <input
                    name="quantity_on_hand"
                    type="number"
                    min="0"
                    value={values.quantity_on_hand}
                    onChange={update}
                  />
                </label>
                <label className="admin-field-label">
                  Low Stock Alert
                  <input
                    name="low_stock_threshold"
                    type="number"
                    min="0"
                    value={values.low_stock_threshold}
                    onChange={update}
                  />
                </label>
              </div>
            </section>
          )}

          <section className="form-section-box">
            <h4 className="section-title">
              {values.colors.length ? '3' : '4'}. General Product Media
            </h4>
            <p className="product-editor-help">
              Optional fallback media used before a color is selected.
            </p>
            <div className="media-input-row">
              <input
                type="url"
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                placeholder="Paste image or video URL (HTTPS)"
              />
              <select
                value={mediaType}
                onChange={(e) => setMediaType(e.target.value)}
              >
                <option value="image">Photo</option>
                <option value="video">Video</option>
              </select>
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={addMedia}
              >
                <SafeIcon icon={mediaType === 'video' ? FiVideo : FiImage} />
                Add
              </button>
            </div>
            <label className="admin-field-label full-width">
              Highlight video (optional — one per product)
              <input
                type="url"
                value={highlightVideo?.media_url || ''}
                onChange={(event) => updateHighlightVideo(event.target.value)}
                placeholder="https://example.com/highlight.mp4"
              />
            </label>
            <p className="product-editor-help">
              Use a direct video URL. The video appears after the photos in the individual product gallery for every color.
            </p>

            {values.media.length === 0 ? (
              <div className="empty-media-box">No general media added.</div>
            ) : (
              <div className="media-preview-grid">
                {values.media.map((item, index) => (
                  <div className="media-preview-item" key={item.id || index}>
                    {item.media_type === 'video' ? (
                      <video src={item.media_url} muted />
                    ) : (
                      <img src={item.media_url} alt="Product" />
                    )}
                    <div className="media-actions">
                      <button
                        type="button"
                        className="action-link remove"
                        onClick={() => removeMedia(index)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <footer className="modal-footer">
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Product'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

export default ProductModal;